import type {
  InspirationAdaptFor,
  InspirationAiField,
  InspirationAiProvenance,
  InspirationMediaType,
  InspirationUsage,
  PortalInspirationEntry,
} from '../../types/portalInspiration.js';
import { normaliseTags } from '../portalLibrary/entryRules.js';
import {
  INSPIRATION_DESCRIPTION_MAX,
  INSPIRATION_TREND_LINE_MAX,
  INSPIRATION_HOWTO_MAX,
  INSPIRATION_TITLE_MAX,
  INSPIRATION_SUGGESTED_LINES_MAX,
  INSPIRATION_SUGGESTED_LINE_MAX,
  isInspirationUsage,
} from './entryRules.js';

// PORTAL INSPIRATION — AI DRAFT, the PURE half. Shared by the browser (frame capture plan) and
// the server (validation + the no-overwrite merge), so the two cannot disagree.

// ---------- frames ----------

/** Video: 2 frames per second, at least 8, at most 40 (Scott, brief §4). */
export const FRAMES_PER_SECOND = 2;
export const MIN_VIDEO_FRAMES = 8;
export const MAX_VIDEO_FRAMES = 40;
/** The long edge a captured frame is downscaled to (px). */
export const IMAGE_MAX_EDGE = 1280;
export const VIDEO_FRAME_MAX_EDGE = 768;
/** A single encoded frame's base64 ceiling (≈ 600 KB of JPEG). */
export const FRAME_MAX_BASE64_CHARS = 800_000;

/**
 * Where to grab frames from a clip of `durationSec` seconds: 2 per second, min 8, cap 40, spread
 * EVENLY and centred in each slice (so a 15 s clip is sampled every ~0.5 s, and a 60 s clip every
 * 1.5 s rather than only its first 20 s). Why dense: these videos hinge on a 1-2 s moment (the
 * paper unfolds, the arm reaches in), and a sparse sample skips the payoff.
 */
export function planFrameTimes(durationSec: number): number[] {
  if (!Number.isFinite(durationSec) || durationSec <= 0) return [0];
  const want = Math.round(durationSec * FRAMES_PER_SECOND);
  const n = Math.min(MAX_VIDEO_FRAMES, Math.max(MIN_VIDEO_FRAMES, want));
  const step = durationSec / n;
  return Array.from({ length: n }, (_, i) => Math.round((step * (i + 0.5)) * 100) / 100);
}

/** The label sent beside each frame: "Frame 7 — 3.0s of 15.2s". */
export function frameLabel(index: number, timeSec: number, durationSec: number): string {
  return `Frame ${index + 1} — ${timeSec.toFixed(1)}s of ${durationSec.toFixed(1)}s`;
}

/** Downscale to a long edge, never upscaling. */
export function scaleToLongEdge(w: number, h: number, maxEdge: number): { w: number; h: number } {
  const long = Math.max(w, h);
  if (long <= maxEdge || long === 0) return { w, h };
  const k = maxEdge / long;
  return { w: Math.max(1, Math.round(w * k)), h: Math.max(1, Math.round(h * k)) };
}

export type FrameSource = 'file' | 'preview' | 'poster';

// ---------- the model's answer ----------

export type InspirationDraft = {
  title: string;
  trendLine: string | null;
  suggestedLines: string[];
  description: string;
  howTo: string;
  suggestedTags: string[];
  suggestedUsage: InspirationUsage;
};

export const DRAFT_MAX_TAGS = 8;

/** JSON schema for structured output. ⚠️ Kept simple on purpose; the real limits are checked in `validateDraft`. */
export const DRAFT_SCHEMA = {
  type: 'object',
  properties: {
    title: { type: 'string' },
    trendLine: { type: 'string' },
    suggestedLines: { type: 'array', items: { type: 'string' } },
    description: { type: 'string' },
    howTo: { type: 'string' },
    suggestedTags: { type: 'array', items: { type: 'string' } },
    suggestedUsage: { type: 'string', enum: ['advertising', 'organic', 'both'] },
  },
  required: ['title', 'trendLine', 'suggestedLines', 'description', 'howTo', 'suggestedTags', 'suggestedUsage'],
  additionalProperties: false,
} as const;

/**
 * ⚠️ A money figure or a percentage is NEVER in a Kyck inspiration card: the house voice says "the
 * offer" and nothing more specific. A draft carrying one is an invented claim and is refused, not trimmed.
 */
const MONEY_OR_PERCENT = /(?:[$£€]\s?\d)|(?:\d\s?%)|(?:\d\s?(?:dollars|bucks)\b)/i;

export type DraftValidation = { ok: true; draft: InspirationDraft } | { ok: false; reason: string };

export function validateDraft(
  raw: unknown,
  mediaType: InspirationMediaType,
  adaptFor: InspirationAdaptFor = 'none',
): DraftValidation {
  if (typeof raw !== 'object' || raw === null) return { ok: false, reason: 'The AI did not return a draft.' };
  const o = raw as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  const title = str(o.title);
  const description = str(o.description);
  const howTo = str(o.howTo);
  const trendLineRaw = str(o.trendLine);
  // An image has no spoken or on-screen line to quote: any trend line on one is dropped, not kept.
  const trendLine = mediaType === 'image' || trendLineRaw === '' ? null : trendLineRaw;
  if (title === '' || description === '' || howTo === '') return { ok: false, reason: 'The AI draft was incomplete.' };
  if (title.length > INSPIRATION_TITLE_MAX) return { ok: false, reason: 'The AI title was too long.' };
  if (trendLine !== null && trendLine.length > INSPIRATION_TREND_LINE_MAX) return { ok: false, reason: 'The AI trend line was too long.' };
  if (description.length > INSPIRATION_DESCRIPTION_MAX) return { ok: false, reason: 'The AI description was too long.' };
  if (howTo.length > INSPIRATION_HOWTO_MAX) return { ok: false, reason: 'The AI how-to was too long.' };
  // ⚠️ Replacement lines exist ONLY when the entry is adapted for a business; otherwise none are kept.
  // A line over the cap is dropped (cutting one mid-sentence changes it), at most three survive.
  const linesIn = Array.isArray(o.suggestedLines) ? o.suggestedLines.filter((t): t is string => typeof t === 'string') : [];
  const suggestedLines =
    adaptFor === 'none'
      ? []
      : [...new Set(linesIn.map((l) => l.trim()).filter((l) => l !== '' && l.length <= INSPIRATION_SUGGESTED_LINE_MAX))].slice(
          0,
          INSPIRATION_SUGGESTED_LINES_MAX,
        );
  if ([title, trendLine ?? '', description, howTo, ...suggestedLines].some((t) => MONEY_OR_PERCENT.test(t))) {
    return { ok: false, reason: 'The AI draft quoted a price or percentage, which is never allowed.' };
  }
  if (!isInspirationUsage(o.suggestedUsage)) return { ok: false, reason: 'The AI did not choose advertising, organic or both.' };
  const tagsIn = Array.isArray(o.suggestedTags) ? o.suggestedTags.filter((t): t is string => typeof t === 'string') : [];
  const suggestedTags = [...new Set(normaliseTags(tagsIn))].filter((t) => t !== '').slice(0, DRAFT_MAX_TAGS);
  return { ok: true, draft: { title, trendLine, suggestedLines, description, howTo, suggestedTags, suggestedUsage: o.suggestedUsage } };
}

// ---------- the no-overwrite merge ----------

export type DraftMerge = {
  entry: PortalInspirationEntry;
  /** Fields the model wrote. */
  written: InspirationAiField[];
  /** Fields left alone because a person set them. */
  kept: InspirationAiField[];
};

/**
 * ⚠️⚠️ AI NEVER OVERWRITES A FIELD A PERSON HAS EDITED (the portalAssets enrichment rule). A field
 * is writable when it is EMPTY or is still listed in `ai.fields` (the model wrote it and nobody has
 * touched it since). Provenance is decided HERE, server-side, never from a request body.
 */
export function mergeDraft(
  entry: PortalInspirationEntry,
  draft: InspirationDraft,
  meta: { model: string; atIso: string; framesSeen: number },
): DraftMerge {
  const aiFields = new Set<InspirationAiField>(entry.ai?.fields ?? []);
  const writable = (field: InspirationAiField, empty: boolean) => empty || aiFields.has(field);
  const next: PortalInspirationEntry = { ...entry };
  const written: InspirationAiField[] = [];
  const kept: InspirationAiField[] = [];
  const apply = (field: InspirationAiField, empty: boolean, set: () => void) => {
    if (writable(field, empty)) {
      set();
      written.push(field);
    } else kept.push(field);
  };

  apply('title', entry.title.trim() === '', () => (next.title = draft.title));
  // A draft with no suggested lines never wipes lines that are there.
  if (draft.suggestedLines.length > 0) apply('suggestedLines', entry.suggestedLines.length === 0, () => (next.suggestedLines = draft.suggestedLines));
  // A draft with no trend line never wipes one that is there.
  if (draft.trendLine !== null) apply('trendLine', entry.trendLine === null || entry.trendLine.trim() === '', () => (next.trendLine = draft.trendLine));
  apply('description', entry.description.trim() === '', () => (next.description = draft.description));
  apply('howTo', entry.howTo.trim() === '', () => (next.howTo = draft.howTo));
  apply('tags', entry.tags.length === 0, () => (next.tags = draft.suggestedTags));
  apply('usage', entry.usage === null, () => (next.usage = draft.suggestedUsage));

  // Still-AI = what was AI before and was not overwritten by a person (only `kept` were person-set,
  // and those never were in aiFields) plus what was just written.
  const stillAi = new Set<InspirationAiField>([...aiFields, ...written]);
  for (const k of kept) stillAi.delete(k);
  const ai: InspirationAiProvenance = {
    model: meta.model,
    at: meta.atIso,
    fields: [...stillAi],
    framesSeen: meta.framesSeen,
  };
  next.ai = ai;
  return { entry: next, written, kept };
}
